"use client";

import type { ReactNode } from "react";

/**
 * SpecRow 右側のアクションボタン定義。
 * モーダルを開く / 外部リンクへ遷移する などを呼び出し側で実装する。
 */
export type SpecRowAction = {
  /** アクションアイコン (例: <CalendarIcon />, <InfoIcon />) */
  icon: ReactNode;
  /** クリックハンドラ。モーダル表示 / 外部リンク遷移など */
  onClick: () => void;
  /** スクリーンリーダー用ラベル */
  ariaLabel: string;
};

/**
 * 部屋詳細のスペック1行を表すコンポーネント。
 *
 * 表現できるバリエーション (props はすべて optional な指定可能):
 * - **アイコン + タイトルのみ**
 * - **+ サブテキスト** (例: "4名" / "ダブル2台")
 * - **+ 注意書き** (例: 貸切露天風呂の予約注意) — `secondary` と併用可
 * - **+ アクションアイコン** (情報 / カレンダー等) — クリックでモーダルや外部リンク
 */
export type SpecRowProps = {
  /** 左側のアイコン (Material Symbol 系) */
  icon: ReactNode;
  /** メインテキスト (16px) */
  title: string;
  /** サブテキスト (14px、タイトル直下) */
  secondary?: string;
  /** 注意書き (12px、`color/text/sub`)。`secondary` と併用可 */
  note?: string;
  /** 右側のアクションボタン */
  action?: SpecRowAction;
};

export function SpecRow({
  icon,
  title,
  secondary,
  note,
  action,
}: SpecRowProps) {
  return (
    // 常に items-start で揃える: 1行 / 複数行いずれの行でも、アイコンと title の top が揃う。
    <div
      className={`flex min-h-[44px] items-start gap-[16px] py-[8px] pl-[16px] ${
        action ? "pr-[12px]" : "pr-[16px]"
      }`}
    >
      {/*
       * アイコンエリアの設計:
       * - 高さ 26px: title の line-box (16px × 1.6 = 25.6px) と同等
       * - items-start: アイコンを div の上端に寄せる
       * - pt-[2px]: 視覚補正オフセット
       *
       * 計算上の half-leading は (25.6 - 16) / 2 ≒ 4.8px だが、Material Symbols の
       * SVG (viewBox 0 -960 960 960) は glyph が viewBox いっぱいではなく内側に
       * 描かれているため、SVG box の top を文字の visible top に揃えると逆に
       * 下がりすぎて見える。視覚最適値として 2px に抑えている。
       */}
      <div className="text-dx-text-default flex h-[26px] shrink-0 items-start justify-center pt-[2px]">
        {icon}
      </div>
      <div className="flex flex-1 flex-col">
        <p className="font-notojp text-dx-text-default text-[16px] leading-[1.6]">
          {title}
        </p>
        {secondary && (
          <p className="font-notojp text-dx-text-default text-[14px] leading-[1.6]">
            {secondary}
          </p>
        )}
        {note && (
          <p className="font-notojp text-dx-text-sub text-[12px] leading-[1.6]">
            {note}
          </p>
        )}
      </div>
      {action && (
        // アクションアイコンも左のアイコン div と同じ視覚補正 (pt-[2px]) を入れて
        // title 文字の visible top に揃える。
        <button
          // 汎用の行部品で用途ごとの id を固定できない。action を渡す呼び出し元も現状無い
          data-analytics-ignore
          type="button"
          onClick={action.onClick}
          className="text-dx-text-default flex h-[26px] w-[40px] shrink-0 items-start justify-center rounded-full pt-[2px]"
          aria-label={action.ariaLabel}
        >
          {action.icon}
        </button>
      )}
    </div>
  );
}
